<template>
  <page-wrapper :title="title" ref="pageEl">
    <div class="wrapper">
      <div class="row">
        <div class="col-3">
          <sc-button
            v-if="!isViewOnlyByRole"
            variant="secondary"
            :route="{ name: 'SubscriberAddTrialPage' }"
            >เพิ่มผู้ทดลองใช้งาน</sc-button
          >
        </div>
        <div class="col-3">
          <sc-button
            v-if="!isViewOnlyByRole"
            variant="danger"
            :route="{ name: 'SubscriberAddOfflinePurchase' }"
            >เพิ่ม Package (ซื้อนอกระบบ)</sc-button
          >
        </div>
      </div>
      <hr />
      <div class="row mb-3">
        <div class="col-2">
          <div class="label mb-2">สถานะ</div>
          <el-select v-model="query.status" clearable>
            <el-option
              v-for="t in status"
              :key="t.value"
              :label="t.label"
              :value="t.value"
            ></el-option>
          </el-select>
        </div>
        <div class="col-2">
          <div class="label mb-2">ประเภท</div>
          <el-select v-model="query.category" clearable>
            <el-option
              v-for="t in category"
              :key="t.value"
              :label="t.label"
              :value="t.value"
            ></el-option>
          </el-select>
        </div>
        <div class="col-2">
          <div class="label mb-2">บ้านเลขที่</div>
          <el-input v-model="query.addressNumber" />
        </div>
        <div class="col-2">
          <div class="label mb-2">โครงการ</div>
          <project-select v-model="selectedProject"></project-select>
        </div>
        <div class="col-2">
          <div class="label mb-2">ชื่อลูกบ้าน</div>
          <el-input v-model="query.firstName" />
        </div>
        <div class="col-2">
          <div class="label mb-2">นามสกุล</div>
          <el-input v-model="query.lastName" />
        </div>
        <div class="col-12 checkbox-only-fast-fix" :style="{
          marginTop: '15px'
        }">
          <el-checkbox
            v-model="isFilteredByFastFix"
            @change="handleSearch"
            :disabled="isFilteredByFastFixDisabled"
            ><p class="checkbox-font">
              แพ็กเกจที่มีบริการ Fast Fix
            </p></el-checkbox
          >
          <el-checkbox
            v-model="isEmptyPricePackage"
            @change="handleSearch"
            :disabled="isEmptyPricePackageDisabled"
            ><p class="checkbox-font">
              แพ็กแรกพบ
            </p></el-checkbox
          >
          <el-checkbox
            v-model="isOnlyTestUser"
            @change="handleSearch"
            :disabled="isOnlyTestUserDisabled"
            ><p class="checkbox-font">
              ดูเฉพาะ User เทส
            </p>
          </el-checkbox>
          <el-checkbox
            v-model="isUnAppointed"
            @change="handleSearch"
            :disabled="isUnAppointedDisabled"
            ><p class="checkbox-font">
              รายการที่ยังไม่ได้นัดหมาย
            </p>
          </el-checkbox>
          <el-checkbox
            v-model="isB2BPackage"
            @change="handleSearch"
            :disabled="isB2BPackageDisabled"
            ><p class="checkbox-font">
              แพ็กเกจ B2B
            </p>
          </el-checkbox>
        </div>
      </div>
      <div v-if="query.homeId">
        ค้นหาด้วย Home Id: {{ query.homeId }}
      </div>
      <div class="text-right">
        <sc-button variant="primary" @click="handleSearch">ค้นหา</sc-button>
      </div>
      <div class="d-flex flex-row mb-2 mt-2">
        <p class="mr-3" v-if="list.length">{{ totalCount }}</p>
      </div>
      <div class="row mb-3">
        <sc-button
          class="ml-3 col-1"
          hidden
          variant="secondary"
          @click="handleClickDownloadReport"
        >Download Report</sc-button>
      </div>
    </div>
    <div class="p-4 text-center" v-if="noResult">ไม่มีข้อมูล</div>
    <template v-else>
      <div class="row-header">
        <div class="cell center">สถานะ</div>
        <div class="cell">ลูกค้า</div>
        <div class="cell">บ้าน</div>
        <div class="cell">แพ็กเกจ</div>
        <div class="cell">ยอดชำระ</div>
        <div class="cell">ประเภท/ซื้อเมื่อ</div>
        <div class="cell center">จำนวน</div>
        <div class="cell">การนัดหมาย (ล่าสุด)</div>
      </div>
      <subscriber-row
        v-for="item in list"
        :key="item.id"
        v-bind="item"
      ></subscriber-row>
    </template>
    <div class="wrapper text-right">
      <el-pagination
        background
        layout="prev, pager, next"
        :total="total"
        :size="10"
        :current-page="query.page"
        @current-change="handleCurrentChange"
      ></el-pagination>
    </div>
  </page-wrapper>
</template>

<script>
import { DateTime } from "luxon";
import PageWrapper from "@/lib/components/PageWrapper";
import ProjectSelect from "@/lib/components/ProjectSelect";
import ScButton from "@/lib/components/ScButton";
import SubscriberRow from "../components/SubscriberRow";
import { subscriptionStatus } from "@/lib/mapper";
import { baanRueJaiClient } from "@/dependencies";
import { Input, Pagination, Select, Option, Checkbox } from "element-ui";
import { getDownloadedReport } from "@/god";
import { isNil } from "ramda";
import { getDurationAppointmentTimeText } from "@/lib/dateTime";

export default {
  components: {
    PageWrapper,
    SubscriberRow,
    ProjectSelect,
    ScButton,
    "el-input": Input,
    "el-pagination": Pagination,
    "el-select": Select,
    "el-option": Option,
    "el-checkbox": Checkbox
  },
  data() {
    const title = "รายการสมาชิก";
    const list = [];
    const query = {
      firstName: null,
      lastName: null,
      page: 1,
      addressNumber: null,
      status: null,
      category: null,
      homeId: null,
      projectId: null
    };
    const reportSubscriptionStatusOption = [
      { label: "ACTIVE" , value: subscriptionStatus.ACTIVE },
      { label: "CANCELLED", value: subscriptionStatus.CANCELLED },
      { label: "UNSUBSCRIBED", value: subscriptionStatus.UNSUBSCRIBED },
      { label: "ONE TIME", value: subscriptionStatus.ONE_TIME }
    ];
    const selectedReportStatus = null;
    const isFilteredByFastFix = false;
    const isEmptyPricePackage = false;
    const isOnlyTestUser = false;
    const isUnAppointed = false;
    const isB2BPackage = false;
    const status = [
      { label: "ทั้งหมด", value: "" },
      { label: "Active", value: "active" },
      { label: "Cancelled", value: "cancelled" },
      { label: "Unsubscribed", value: "unsubscribed" },
      { label: "Expired", value: "expired" }
    ];
    const category = [
      { label: "ทั้งหมด", value: "" },
      { label: "Free Trial", value: "freeTrial" },
      { label: "One Time", value: "oneTime" },
      { label: "Subscribe", value: "subscribe" }
    ];
    const permission = JSON.parse(localStorage.getItem("adminRolePermission")) || null;
    const selectedProject = null;

    return {
      title,
      list,
      query,
      total: 0,
      reportSubscriptionStatusOption,
      selectedReportStatus,
      isFilteredByFastFix,
      isEmptyPricePackage,
      isOnlyTestUser,
      isUnAppointed,
      isB2BPackage,
      status,
      category,
      permission,
      selectedProject
    };
  },

  computed: {
    totalCount() {
      const limit = 10;
      const total = this.total.toLocaleString();
      const currentTotalItem = this.list.length;
      const isLastPage = limit > currentTotalItem;
      const currentPage = this.query.page;
      const end = (isLastPage ? total : currentPage * limit).toLocaleString();
      const start = (
        (isLastPage ? limit * currentPage - limit : end - limit) + 1
      ).toLocaleString();

      return `แสดงผล ${start} - ${end} จากทั้งหมด ${total}`;
    },
    noResult() {
      return this.list.length === 0;
    },
    queryParams() {
      this.selectedProject = {
        projectId: this.$route.query.projectId,
        projectName: this.$route.query.projectId
      };
      const queryParams = this.$route.query.serviceType
      ? {
          firstName: this.query.firstName,
          lastName: this.query.lastName,
          page: this.query.page,
          addressNumber: this.query.addressNumber,
          serviceType: this.$route.query.serviceType,
          status: this.query.status,
          category: this.query.category,
          homeId: this.query.homeId,
          projectId: this.query.projectId
        }
      : {
          firstName: this.query.firstName,
          lastName: this.query.lastName,
          page: this.query.page,
          addressNumber: this.query.addressNumber,
          status: this.query.status,
          category: this.query.category,
          homeId: this.query.homeId,
          projectId: this.query.projectId
        };

      if (this.isEmptyPricePackage) {
        queryParams.isEmptyPricePackage = this.isEmptyPricePackage;
      }

      if (this.isOnlyTestUser) {
        queryParams.isOnlyTestUser = this.isOnlyTestUser;
      }

      if (this.isUnAppointed) {
        queryParams.isUnAppointed = this.isUnAppointed;
      }

      if (this.isB2BPackage) {
        queryParams.isB2BPackage = this.isB2BPackage;
      }

      return queryParams;
    },
    showAddFreeTrialButton() {
      return !this.$isCallCenter;
    },
    showReportButton() {
      return !this.$isCallCenter;
    },
    reportStatus() {
      return this.selectedReportStatus;
    },
    isViewOnlyByRole() {
      const roles = ["AC","CC"]
      return roles.includes(this.permission.role)
    },
    isFilteredByFastFixDisabled() {
      return this.isUnAppointed || this.isB2BPackage;
    },
    isEmptyPricePackageDisabled() {
      return this.isUnAppointed || this.isB2BPackage;
    },
    isOnlyTestUserDisabled() {
      return this.isUnAppointed || this.isB2BPackage;
    },
    isUnAppointedDisabled() {
      return this.isFilteredByFastFix || this.isEmptyPricePackage || this.isOnlyTestUser || this.isB2BPackage;
    },
    isB2BPackageDisabled() {
      return this.isFilteredByFastFix || this.isEmptyPricePackage || this.isOnlyTestUser || this.isUnAppointed;
    },
  },
  created() {
    this.setup();
  },
  watch: {
    async "$route.query"(query) {
      this.setupQuery();
      await this.setupData();

      this.$refs.pageEl.$el.querySelector(".page-content").scrollTop = 0;
    }
  },
  methods: {
    setup() {
      this.setupQuery();
      this.setupData();
    },
    setupQuery() {
      try {
        this.query = {
          firstName: this.$route.query.firstName || null,
          lastName: this.$route.query.lastName || null,
          page: parseInt(this.$route.query.page) || 1,
          addressNumber: this.$route.query.addressNumber || undefined,
          status: this.$route.query.status || null,
          category: this.$route.query.category || null,
          homeId: this.$route.query.homeId || null,
          projectId: this.$route.query.projectId || null
        };

        this.isFilteredByFastFix = !isNil(this.$route.query.serviceType);
        this.isEmptyPricePackage = this.$route.query.isEmptyPricePackage;
        this.isOnlyTestUser = this.$route.query.isOnlyTestUser;
        this.isUnAppointed = this.$route.query.isUnAppointed;
        this.isB2BPackage = this.$route.query.isB2BPackage;
      } catch (err) {
        this.query = {
          firstName: this.$route.query.firstName || null,
          lastName: this.$route.query.lastName || null,
          page: 1,
          addressNumber: this.$route.query.addressNumber || undefined,
          status: this.$route.query.status,
          category: this.$route.query.category,
          homeId: this.$route.query.homeId || null,
          projectId: this.$route.query.projectId || null
        };

        this.isFilteredByFastFix = !isNil(this.$route.query.serviceType);
        this.isEmptyPricePackage = this.$route.query.isEmptyPricePackage;
        this.isOnlyTestUser = this.$route.query.isOnlyTestUser;
        this.isUnAppointed = this.$route.query.isUnAppointed;
        this.isB2BPackage = this.$route.query.isB2BPackage;
      }
    },
    async setupData() {
      await this.setupList();
    },
    async setupList() {
      const params = this.queryParams;

      const { count, subscribers } = await this.fetchList(params);

      this.total = count;

      this.list = subscribers.map(item => {
        const id = item.id;
        const userName = `${item.owner.first_name} ${item.owner.last_name}`;
        const phoneNumber = item.owner.mobile_number || "-";
        const offlinePurchase = (item.is_user_for_offline_purchase_by_admin)
          ? 'ซื้อนอกระบบลูกค้าไม่โหลด App'
          : '';

        const user = { name: userName, phoneNumber, offlinePurchase };
        const home = {
          addressNumber: item.home ? item.home.address_no : "",
          project: item.project ? item.project.project_name : ""
        };

        const invoice = item.invoices[0];

        const pack = {
          name: item.current_package.name,
          price: item.price
           ? item.price
           : item.current_package.price,
          billingCycle: item.current_package.billing_cycle,
          couponCode: invoice && invoice.coupon && invoice.coupon.code,
          discountAmount: invoice && invoice.coupon && invoice.coupon.discount_amount,
          paymentAmount: invoice && invoice.total,
          totalCoinAmount: invoice && invoice.total_coin_amount,
          originalPrice: item.current_package.original_price,
        };
        const route = { name: "SubscriberDetailPage", params: { id } };

        const purchasedAt = DateTime.fromISO(item.purchased_at).toUTC();
        const renewAt = DateTime.fromISO(item.renew_at).toUTC();
        const status = item.status;
        const appointmentDate =
          item.current_appointment && item.current_appointment.date_time
            ? DateTime.fromISO(item.current_appointment.date_time).toUTC()
            : undefined;

        const appointmentDurationTime = getDurationAppointmentTimeText(
          item.current_appointment && item.current_appointment.date_time,
          item.current_appointment && item.current_appointment.date_time_end
        )

        const category = item.category;
        const quantity = item.quantity || 1;

        return {
          id,
          user,
          home,
          package: pack,
          route,
          purchasedAt,
          renewAt,
          status,
          appointmentDate,
          category,
          quantity,
          appointmentDurationTime
        };
      });
    },
    async fetchList(params) {
      const response = await baanRueJaiClient.admin.subscriptions.subscribers.list(
        params
      );

      return response.data.result;
    },
    async handleSearch() {
      const projectId = this.selectedProject
        ? this.selectedProject.projectId
        : null
      const query = this.isFilteredByFastFix
        ? {
            ...this.query,
            projectId,
            serviceType: "FAST_FIX",
            page: 1
          }
        : { ...this.query, projectId, page: 1 };
      const route = {
        name: this.$route.name,
        query
      };

      if (this.isEmptyPricePackage) {
        query.isEmptyPricePackage = this.isEmptyPricePackage;
      }

      if (this.isOnlyTestUser) {
        query.isOnlyTestUser = this.isOnlyTestUser;
      }

      if (this.isUnAppointed) {
        query.isUnAppointed = this.isUnAppointed;
      }

      if (this.isB2BPackage) {
        query.isB2BPackage = this.isB2BPackage;
      }

      this.$router.replace(route);
    },
    handleCurrentChange(page) {
      const route = {
        name: this.$route.name,
        query: { ...this.$route.query, page }
      };

      this.$router.replace(route);
    },
    handleChangeReportStatus(value) {
      this.selectedReportStatus = value;
    },
    async handleClickDownloadReport() {
      const params = {
        status: this.query.status || this.$route.query.status,
        category: this.query.category || this.$route.query.category
      }

      if (!this.query.status && !this.query.category) {
        alert("กรุณาเลือกสถานะหรือประเภทก่อน Download Report");
        return;
      }

      try {
        const response = await baanRueJaiClient.admin.subscriptions.subscribers.getReport(
          params
        );

        getDownloadedReport(response.data, `list-subscriber-${params.status || params.category}.xlsx`);
      } catch(error) {
        // TODO: use alert message from response message backend
        alert("ไม่สามารถ Download Report ได้ เนื่องจากไม่มีข้อมูลหรือเกิดข้อผิดพลาด");
        console.error(error);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.wrapper {
  padding: ($space / 2) $space;
}

.row-header {
  display: grid;
  grid-template-columns: 120px repeat(4, 1fr) 200px 100px repeat(1, 1fr);
  padding: ($space / 2) $space;

  grid-gap: 15px;

  border-bottom: $border;

  font-weight: bold;

  > .center {
    text-align: center;
  }
}

.header {
  padding: 30px;
  border-bottom: $border;
}

.checkbox {
  &-only-fast-fix {
    margin-top: 0.118rem;
  }

  &-font {
    font-size: 16px;
  }
}

.download-report-message {
  font-size: 15px;
}
</style>



// WEBPACK FOOTER //
// src/features/subscriptions/modules/subscribers/pages/SubscriberListPage.vue